import { formatISO } from 'date-fns'
import { v4 as uuidV4 } from 'uuid'
import { OpalApiBFFComponent } from '@opallabs/api'
import { type CategoryType } from '@opallabs/concepts'
import {
  computeDurationFromDates,
  computeEndDateFromDuration,
} from '@opallabs/lambda'
import { factories } from '@opallabs/starman'
import { prePlanningBlockKey } from '..'
import { PlanResponse } from '../types'
import { resolveOverlappingBlocks } from './resolveOverlappingBlocks'

type BlockParams = Parameters<
  typeof factories.asgardBff.components.noramlizedBlock.build
>[0]

type PlanBlock = PlanResponse['data']['blocks']['by_id'][string]

/**
 * Since the factory can override any parameter for a block, we'll
 * reduce down the interface to those parameters that are relevant for
 * creating the block in the UI.
 */
export type InsertBlockParams = {
  id?: string
  categoryType: CategoryType | null
  endDate: Date | null
  momentId: string | null
  planId: string
  rowIndex: number | null
  startDate: Date | null
  swimlaneId: string | null
  title: string
  ownerId: string
}

const optionalShortDate = (date: Date | null) =>
  date ? formatISO(date, { representation: 'date' }) : null

const buildFactoryBlockParams = (params: InsertBlockParams) => {
  return {
    duration:
      params.startDate && params.endDate
        ? computeDurationFromDates(params.startDate, params.endDate)
        : null,
    id: params.id ?? `temp-${uuidV4()}`,
    owner_id: params.ownerId,
    paired_moment_id: params.momentId || null,
    row_index: params.rowIndex ?? 0,
    start_at: optionalShortDate(params.startDate),
    category_id: params.swimlaneId,
    title: params.title,
    category_type: params.categoryType
      ? { id: params.categoryType.id, name: params.categoryType.name }
      : null,
  } satisfies BlockParams
}

export const createPlaceholderBlock = (params: InsertBlockParams) =>
  factories.asgardBff.components.noramlizedBlock.build(
    buildFactoryBlockParams(params)
  ) satisfies PlanBlock

export const insertBlock = (newBlock: PlanBlock) => {
  return (draftResponse: PlanResponse) => {
    draftResponse.data.blocks.by_id[newBlock.id] = newBlock

    const blockPosition = {
      id: newBlock.id,
      start_at: newBlock.start_at,
      duration: newBlock.duration,
      end_at: computeEndDateFromDuration(newBlock.start_at, newBlock.duration),
      row_index: newBlock.row_index,
    } satisfies OpalApiBFFComponent<'swimlane_position'>

    const categoryPositionMap =
      draftResponse.data.blocks.by_category_with_position

    if (isPreplanningBlock(newBlock)) {
      if (!categoryPositionMap[prePlanningBlockKey]) {
        categoryPositionMap[prePlanningBlockKey] = []
      }
      categoryPositionMap[prePlanningBlockKey].push(blockPosition)
    }

    if (newBlock.category_id) {
      if (!categoryPositionMap[newBlock.category_id]) {
        categoryPositionMap[newBlock.category_id] = []
      }
      categoryPositionMap[newBlock.category_id].push(blockPosition)
    }

    return draftResponse
  }
}

/**
 * Convenience wrapper that combines `insertBlock` and `resolveOverlappingBlocks`, ensuring the inserted block doesn’t
 * create layout problems. Particularly relevant when the user is in the Timeline view and creates a block from a
 * template that has a duration — the template’s duration overrides the implicit end date of the `<Placeholder>` where
 * the new block is being created.
 */
export const insertBlockResolvingOverlaps = (newBlock: PlanBlock) => {
  return (draftResponse: PlanResponse) => {
    let result = insertBlock(newBlock)(draftResponse)
    if (newBlock.category_id) {
      result = resolveOverlappingBlocks(
        newBlock.id,
        newBlock.category_id
      )(result)
    }
    return result
  }
}

/**
 * "Preplanning" blocks are blocks that appear on the table but not timeline,
 * i.e. they don't have a start_at or category_id to place them on the timeline.
 */
const isPreplanningBlock = (block: PlanBlock) => {
  return block.start_at === null || block.category_id === null
}
